import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import {
  API,
  ChannelFullDTO,
  DeviceAttributeDTO,
  DeviceDTO,
  LocationDTO,
  LocationFullDTO,
  MediumTransformationDTO,
  OwnedScreenDTO,
  Pagination,
  PlayerHardwareDTO,
  PlayerSoftwareDTO,
  PoolDTO,
  ScreenDTO,
  ScreenHardwareDTO,
} from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { DeviceFullDTO } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { SearchSelectResult } from 'src/app/services/gui/modal/modal.model';
import {
  BehaviorSubject,
  concatMap,
  delay,
  forkJoin,
  from,
  lastValueFrom,
  Observable,
  Subject,
  Subscription,
  retryWhen,
  of,
  scan,
} from 'rxjs';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { MsOptions, Mutable } from '../../../../services/global/global-interfaces';
import { PermissionService } from '../../../../services/permission.service';
import { take, takeUntil, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-comp-device-creation',
  templateUrl: './device-creation.component.html',
  styleUrls: ['./device-creation.component.scss'],
  standalone: false,
})
export class DeviceCreationComponent implements OnInit, ModalNavigationComponent, OnDestroy {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  blockBackdrop: boolean = false;

  isInModalContext: boolean;

  pools: PoolDTO[] = [];

  channels: ChannelFullDTO[] = [];
  attributes: DeviceAttributeDTO[] = [];
  screenHardwareList: ScreenHardwareDTO[] = [];
  mediumTransformations: MediumTransformationDTO[] = [];
  screens: OwnedScreenDTO[] = [];

  pool: PoolDTO;

  channel: ChannelFullDTO;

  location: LocationDTO;

  productive: boolean;
  offline: boolean;
  external: boolean;
  disabled: boolean;

  playerHardware: PlayerHardwareDTO;
  playerSoftware: PlayerSoftwareDTO;
  playerHardwareList: PlayerHardwareDTO[];
  playerSoftwareList: PlayerSoftwareDTO[];

  identifier: string;
  name: string;

  msPlayerHardwareOptions: MsOptions;
  msPlayerSoftwareOptions: MsOptions;
  msScreenHardwareOptions: MsOptions;
  msMediumTransformationsOptions: MsOptions;

  amount: number;
  showCreationQueue: boolean;
  currentCreationIndex: number;
  maxCreationIndex: number;
  $destroyCreation: Subject<any> = new Subject<any>();
  subs: Subscription[] = [];
  isDeviceMobile: boolean;
  duration: number;

  constructor(
    private readonly api: API,
    private readonly modalNavigation: ModalNavigationService,
    private readonly modal: ModalService,
    private readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService
  ) {
    this.isDeviceMobile = false;
    this.duration = 60;
    this.blockBackdrop = false;
    this.identifier = '';
    this.name = '';
    this.amount = 1;

    this.showCreationQueue = false;
    this.currentCreationIndex = 0;
    this.maxCreationIndex = 0;
  }

  ngOnInit() {
    this.initPoolsAndChannelsAndAttributesAndMediumTransformations().then(() => {
      this.init();
    });
  }

  initPoolsAndChannelsAndAttributesAndMediumTransformations() {
    return new Promise<void>(res => {
      const observablesList = [];
      const pagination = new BehaviorSubject<Pagination>({
        page: 1,
        count: 99,
        pages: -1,
        resultcount: -1,
      });
      if (this.permissionService.hasPermission(this.permissionService.Permission.pool_list)) {
        observablesList.push(this.api.ss_nm_Pool_retrieveAll(null, pagination));
      }
      if (this.permissionService.hasPermission(this.permissionService.Permission.medium_transformation_list)) {
        observablesList.push(this.api.ss_nm_MediumTransformation_retrieveAll(null, pagination));
      }
      if (this.permissionService.hasPermission(this.permissionService.Permission.channel_list)) {
        observablesList.push(this.api.ss_nm_Channel_retrieveAll({ locked: true }, pagination));
      }
      if (this.permissionService.hasPermission(this.permissionService.Permission.device_attributes_edit)) {
        observablesList.push(this.api.ca_md_DeviceAttribute_retrieveAllDatatypes(null, pagination));
      }

      if (observablesList.length > 0) {
        const sub = forkJoin(observablesList)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe(resultArray => {
            const pools: PoolDTO[] = [];
            const channels: ChannelFullDTO[] = [];
            const attributes: DeviceAttributeDTO[] = [];
            const mediumTransformations: MediumTransformationDTO[] = [];
            if (resultArray && resultArray.length > 0) {
              for (const result of resultArray) {
                if (Array.isArray(result)) {
                  const arr: any[] = result;
                  for (const item of arr) {
                    switch (item._type) {
                      case 'ChannelFullDTO':
                      case 'ChannelDTO':
                        channels.push(item);
                        break;
                      case 'MediumTransformationDTO':
                        mediumTransformations.push(item);
                        break;
                      case 'PoolHierarchyDTO':
                      case 'PoolDTO':
                        pools.push(item);
                        break;
                      case 'DeviceAttributeDatatypeDTO':
                        if (item.adPlacerAbleToSet) {
                          const attr = new DeviceAttributeDTO();
                          attr.id = { datatype: item };
                          attr.datatype = item;
                          attr.name = item.name;
                          attr.value = item.defaultValue;
                          attributes.push(attr);
                        }
                        break;
                    }
                  }
                } else {
                  const item: any = result;
                  switch (item._type) {
                    case 'ChannelFullDTO':
                    case 'ChannelDTO':
                      channels.push(item);
                      break;
                    case 'MediumTransformationDTO':
                      mediumTransformations.push(item);
                      break;
                    case 'PoolHierarchyDTO':
                    case 'PoolDTO':
                      pools.push(item);
                      break;

                    case 'DeviceAttributeDatatypeDTO':
                      if (item.adPlacerAbleToSet) {
                        const attr = new DeviceAttributeDTO();
                        attr.id = { datatype: item };
                        attr.datatype = item;
                        attr.name = item.name;
                        attr.value = item.defaultValue;
                        attributes.push(attr);
                      }
                      break;
                  }
                }
              }
              this.attributes = attributes;
              this.pools = pools;
              this.mediumTransformations = mediumTransformations;
              this.channels = channels;
              if (this.channels && this.channels.length > 0) {
                this.channel = this.channels[0];
              }
              res();
            } else {
              res();
            }
          });
        this.subs.push(sub);
      } else {
        // observablesList <= 0
        res();
      }
    });
  }

  init() {
    if (this.modalNavigation.getCurrentNavigation().data) {
      if (!this.modalNavigation.getSharedData().deviceFlags) {
        this.modalNavigation.getSharedData().deviceFlags = {};
      }

      const paramData = this.modalNavigation.getCurrentNavigation().data;
      const devflags = this.modalNavigation.getSharedData().deviceFlags;

      if (paramData?.useParamData) {
        devflags.pool = paramData.pool;
        devflags.channel = paramData.channel;
        devflags.location = paramData.location;

        this.location = paramData.location;

        this.modalNavigation.getCurrentNavigation().data.useParamData = false;
      }

      this.modalNavigation.getCurrentNavigation().data = {};
    }
    if (this.modalNavigation.getSharedData().locations?.length >= 1) {
      this.location = this.modalNavigation.getSharedData().locations[0];
    }
    if (this.modalNavigation.getSharedData().deviceFlags) {
      const devflags = this.modalNavigation.getSharedData().deviceFlags;
      if (devflags.pool) {
        this.pool = devflags.pool;
      }
      if (devflags.channel) {
        this.channel = devflags.channel;
      }
      if (devflags.offline) {
        this.offline = devflags.offline;
      }
      if (devflags.productive) {
        this.productive = devflags.productive;
      }
      if (devflags.disabled) {
        this.disabled = devflags.disabled;
      }
      if (devflags.external) {
        this.external = devflags.external;
      }
      this.identifier = devflags.identifier;
      this.name = devflags.name;
    } else {
      this.modalNavigation.getSharedData().deviceFlags = {};
    }
    this.createScreens();
  }

  getPoolText() {
    if (this.pool) {
      return this.pool.name;
    }
    return 'n. def.';
  }

  setPool(pool: PoolDTO) {
    this.pool = pool;
  }

  getChannelText() {
    if (this.channel) {
      return this.channel.name;
    }
    return 'n. def.';
  }

  setChannel(channel: ChannelFullDTO) {
    this.channel = channel;
    this.createScreens();
  }

  private createScreens() {
    this.screens =
      this.channel?.markers?.map(marker => {
        const screen = new OwnedScreenDTO();
        screen.marker = { ...marker, channel: this.channel };
        return screen;
      }) ?? [];
  }

  getLocationText() {
    if (this.location) {
      return this.stringTransformation.locationOrCompanyToShortString(this.location);
    }
    return 'n. def.';
  }

  selectLocation() {
    this.modalNavigation.getSharedData().deviceFlags.pool = this.pool;
    this.modalNavigation.getSharedData().deviceFlags.channel = this.channel;
    this.modalNavigation.getSharedData().deviceFlags.external = this.external;
    this.modalNavigation.getSharedData().deviceFlags.offline = this.offline;
    this.modalNavigation.getSharedData().deviceFlags.disabled = this.disabled;
    this.modalNavigation.getSharedData().deviceFlags.productive = this.productive;
    this.modalNavigation.getSharedData().deviceFlags.identifier = this.identifier;
    this.modalNavigation.getSharedData().deviceFlags.name = this.name;

    const initialLoc = this.location;
    this.modal
      .searchSelect({
        attributeType: 'LOCATION',
        datatype: null,
        singlemode: true,
        initial: initialLoc ? [initialLoc] : null,
        title: this.translate.instant('lang.globals.selectValueForNamedFilter', { name: this.translate.instant('lang.globals.location') }),
      })
      .then((e: SearchSelectResult<LocationDTO>) => {
        if (!e.cancel) {
          if (e.empty) {
            this.location = null;
          } else if (e.values) {
            this.location = e.values[0];
          } else {
            this.location = null;
          }
        }
      })
      .catch(() => {});
  }

  onAmount($event: number) {
    if ($event > 0) {
      this.amount = $event;
    } else {
      this.amount = 1;
    }
  }

  onIdentifier($event: string) {
    if ($event?.length > 0) {
      this.identifier = $event;
    } else {
      this.identifier = null;
    }
  }

  onName($event: string) {
    if ($event?.length > 0) {
      this.name = $event;
    } else {
      this.name = null;
    }
  }

  msUpdate($event: any[], type: string, pScreen?: ScreenDTO) {
    if (!pScreen && (type === 'MEDIUM_TRANSFORMATION' || type === 'SCREEN_HARDWARE')) {
      return;
    }
    const deviceScreen = this.screens.find(screen => screen.id === pScreen?.id);
    switch (type) {
      case 'PLAYER_HARDWARE':
        this.playerHardware = null;
        if ($event?.length > 0) {
          this.playerHardware = $event[0];
        }
        break;

      case 'PLAYER_SOFTWARE':
        this.playerSoftware = null;
        if ($event?.length > 0) {
          this.playerSoftware = $event[0];
        }
        break;
      case 'SCREEN_HARDWARE':
        deviceScreen.hardware = null;
        if ($event?.length > 0) {
          deviceScreen.hardware = $event[0];
        }
        break;
      case 'MEDIUM_TRANSFORMATION':
        deviceScreen.mediumTransformation = null;
        if ($event?.length > 0) {
          deviceScreen.mediumTransformation = $event[0];
        }
        break;
    }
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'SCREEN_HARDWARE':
        if (options) {
          this.msScreenHardwareOptions = options;
        }
        if (this.msScreenHardwareOptions?.pagination) {
          const sub = this.api
            .ca_md_ScreenHardware_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msScreenHardwareOptions?.search),
              },
              this.msScreenHardwareOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: ScreenHardwareDTO[]) => {
                this.screenHardwareList = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;
      case 'MEDIUM_TRANSFORMATION':
        if (options) {
          this.msMediumTransformationsOptions = options;
        }
        if (this.msMediumTransformationsOptions?.pagination) {
          const sub = this.api
            .ss_nm_MediumTransformation_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msMediumTransformationsOptions?.search),
              },
              this.msMediumTransformationsOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: MediumTransformationDTO[]) => {
                this.mediumTransformations = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;
      case 'PLAYER_HARDWARE':
        if (options) {
          this.msPlayerHardwareOptions = options;
        }
        if (this.msPlayerHardwareOptions?.pagination) {
          const sub = this.api
            .ca_md_PlayerHardware_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlayerHardwareOptions?.search),
              },
              this.msPlayerHardwareOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: PlayerHardwareDTO[]) => {
                this.playerHardwareList = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;

      case 'PLAYER_SOFTWARE':
        if (options) {
          this.msPlayerSoftwareOptions = options;
        }
        if (this.msPlayerSoftwareOptions?.pagination) {
          const sub = this.api
            .ca_md_PlayerSoftware_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlayerSoftwareOptions?.search),
              },
              this.msPlayerSoftwareOptions?.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (p: PlayerSoftwareDTO[]) => {
                this.playerSoftwareList = p;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
          this.subs.push(sub);
        }
        break;
    }
  }

  ok() {
    this.blockBackdrop = true;
    const dev: Mutable<DeviceFullDTO> = new DeviceFullDTO();
    dev.pool = this.pool;
    dev.productive = this.productive ? this.productive : false;
    dev.offline = this.offline ? this.offline : false;
    dev.external = this.external ? this.external : false;
    dev.disabled = this.disabled ? this.disabled : false;
    dev.location = this.location as LocationFullDTO;
    dev.identifier = this.identifier;
    dev.name = this.name;
    dev.attributes = this.attributes;

    dev.hardware = this.playerHardware;
    dev.software = this.playerSoftware;
    dev.screens = this.screens;

    if (this.isDeviceMobile) {
      dev.positionValidityDuration = this.duration;
    } else {
      dev.positionValidityDuration = null;
    }

    const obsArray: Observable<DeviceDTO>[] = [];
    let obs: Observable<DeviceDTO> = null;
    for (let devIndex = 0; devIndex < this.amount; devIndex++) {
      if (this.amount > 1) {
        const devSuffix = '_' + this.stringTransformation.addLeadingZeros(devIndex + 1, this.amount.toString().length);
        dev.name = this.name + devSuffix;
        dev.identifier = this.identifier + devSuffix;
      }
      if (dev.location?.id) {
        obs = this.api.ss_nm_Channel_createDeviceAtLocation(this.channel.id, dev.location.id, dev);
      } else {
        obs = this.api.ss_nm_Channel_createDevice(this.channel.id, dev);
      }
      obsArray.push(obs);
    }
    this.showCreationQueue = true;
    this.currentCreationIndex = 0;
    this.maxCreationIndex = obsArray.length;
    lastValueFrom(
      from(obsArray).pipe(
        concatMap((creationObs, index) =>
          creationObs.pipe(
            // retry up to 10 times with 500ms delay
            retryWhen(errors =>
              errors.pipe(
                scan((retryCount, error) => {
                  if (retryCount >= 9) {
                    throw error; // rethrow error after 10th try
                  }
                  return retryCount + 1;
                }, 0),
                delay(500)
              )
            ),
            // delay after each successful call (except the last one)
            concatMap(result => (index === obsArray.length - 1 ? of(result) : of(result).pipe(delay(100)))),
            tap(() => {
              // increase screen counter for user feedback
              this.currentCreationIndex = index + 1;
            })
          )
        ),
        takeUntil(this.$destroyCreation)
      )
    )
      .then(e => {
        this.close(e);
      })
      .catch(error => {
        this.errorHandler.errorHandler(error);
      })
      .finally(() => {
        this.blockBackdrop = false;
      });
  }

  destroyCreationProcess() {
    this.$destroyCreation.next(null);
    this.$destroyCreation.complete();
  }

  close(e) {
    this.modalNavigation.result(e);
  }

  cancel() {
    this.close(null);
  }

  updateAttribute(attribute: DeviceAttributeDTO, $event: string[]) {
    attribute.value = $event.join(', ');
  }

  getAttributeInputData(attribute: DeviceAttributeDTO) {
    if (!attribute.value) {
      return [];
    }
    return attribute.value.split(',').map(value => value.trim());
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];
  }
}
